import { useState, useMemo, useEffect } from 'react'
import {
  Container,
  Title,
  Stack,
  Grid,
  Card,
  Text,
  Group,
  Select,
  Button,
  Loader,
  Center,
  Badge,
  Table,
  Paper,
  TextInput,
  MultiSelect,
  ScrollArea,
  Pagination,
  RingProgress,
  Tooltip,
} from '@mantine/core'
import { DatePickerInput } from '@mantine/dates'
import { IconCalendar, IconFilter, IconRefresh, IconSearch, IconDownload, IconLock, IconLoader } from '@tabler/icons-react'
import { useComplianceDashboard, useJurisdictions, useOffensesBySearchId } from '@/hooks/useAnalytics'
import { exportComplianceDashboard } from '@/services/analytics.api'
import { usePermissions } from '@/hooks/usePermissions'
import { useVersionContext } from '@/context/VersionContext'
import { PermissionGate } from '@/components/PermissionGate'
import { AdminOnlyBadge } from '@/components/RoleInfoBanner'
import { OffenseDetailDialog } from '@/components/OffenseDetailDialog'
import { SendToExpertButton } from '@/components/SendToExpertButton'
import type { ComplianceDashboardData, ComplianceStatistics } from '@/types'

const fmt = (v: number | null | undefined) => (v ?? 0).toLocaleString()

export function ReportabilityDashboard() {
  const { canExportFilteredData, isAdmin } = usePermissions()

  // Dialog state for offense details
  const [selectedOffense, setSelectedOffense] = useState<ComplianceDashboardData | null>(null)
  const [dialogOpened, setDialogOpened] = useState(false)

  // Fetch all offenses for the selected search_id from database
  const { data: offensesBySearchData } = useOffensesBySearchId(selectedOffense?.search_id || null)

  // Date range (last 7 days by default)
  const [dateRange, setDateRange] = useState<[Date | null, Date | null]>([
    new Date(Date.now() - 1 * 24 * 60 * 60 * 1000),
    new Date(),
  ])
  const [jurisdiction, setJurisdiction] = useState<string | null>(null)

  // Table filters
  const [searchId, setSearchId] = useState('')
  const [segmentFilter, setSegmentFilter] = useState<string[]>([])
  const [matchFilter, setMatchFilter] = useState<string[]>([])
  const [errorTypeFilter, setErrorTypeFilter] = useState<string[]>([])
  const [dispositionFilter, setDispositionFilter] = useState<string[]>([])
  const [crimeCategoryFilter, setCrimeCategoryFilter] = useState<string[]>([])
  const [adjudicationFilter, setAdjudicationFilter] = useState<string[]>([])
  const [courtTypeFilter, setCourtTypeFilter] = useState<string[]>([])
  const [mismatchCategoryFilter, setMismatchCategoryFilter] = useState<string[]>([])

  // Pagination
  const [page, setPage] = useState(1)
  const pageSize = 50

  // Export loading state
  const [isExporting, setIsExporting] = useState(false)

  // Column selection - all columns available
  const allAvailableColumns = [
    { value: 'search_id', label: 'Search ID' },
    { value: 'offense_id', label: 'Offense ID' },
    { value: 'case_number', label: 'Case Number' },
    { value: 'offense_type', label: 'Offense Type' },
    { value: 'type_class', label: 'Type Class' },
    { value: 'subtype_class', label: 'Subtype Class' },
    { value: 'charge', label: 'Charge' },
    { value: 'charge_description', label: 'Charge Desc' },
    { value: 'charge_norm', label: 'Charge Norm' },
    { value: 'disposition', label: 'Disposition' },
    { value: 'disp_class', label: 'Disp Class' },
    { value: 'disposition_norm', label: 'Disp Norm' },
    { value: 'charge_date', label: 'Charge Date' },
    { value: 'arrest_date', label: 'Arrest Date' },
    { value: 'disposition_date', label: 'Disp Date' },
    { value: 'offense_age', label: 'Offense Age' },
    { value: 'age_category', label: 'Age Category' },
    { value: 'age_at_offense', label: 'Age at Offense' },
    { value: 'offense_status', label: 'Offense Status' },
    { value: 'adjudication_category', label: 'Adjudication' },
    { value: 'crime_category', label: 'Crime Category' },
    { value: 'court_type', label: 'Court Type' },
    { value: 'additional_info', label: 'Additional Info' },
    { value: 'sentence_info', label: 'Sentence Info' },
    { value: 'effective_release_date', label: 'Release Date' },
    { value: 'sentence_is_serving', label: 'Currently Serving' },
    { value: 'ce_decision', label: 'AI Decision' },
    { value: 'rules_applied', label: 'Rules Applied' },
    { value: 'rationale', label: 'AI Rationale' },
    { value: 'ground_truth', label: 'UBS Decision' },
    { value: 'all_notes', label: 'Notes' },
    { value: 'segment', label: 'Segment' },
    { value: 'match_status', label: 'Match' },
    { value: 'error_type', label: 'Error' },
    { value: 'actions', label: 'Actions' },
  ]

  // Columns that are restricted for Expert role (Admin can see all)
  const adminOnlyColumns = [
    'offense_type',            // Classification detail - Admin only
    'type_class',              // Classification detail - Admin only
    'subtype_class',           // Classification detail - Admin only
    'charge_norm',             // Normalized charge - Admin only
    'disp_class',              // Disposition classification - Admin only
    'disposition_norm',               // Normalized disposition - Admin only
    'offense_age',             // Age calculation - Admin only
    'offense_status',          // Status detail - Admin only
    'age_at_offense',          // Age calculation - Admin only
    'age_category',            // Age grouping - Admin only
    'adjudication_category',        // Adjudication category - Admin only
    'crime_category',          // Crime grouping - Admin only
    'court_type',              // Court classification - Admin only
    'effective_release_date',
    'sentence_is_serving',
    'actions',                 // Action buttons - Admin only
  ]

  // Filter available columns based on role
  const availableColumnsForRole = useMemo(() => {
    if (isAdmin) {
      return allAvailableColumns // Admin sees everything
    }

    // Expert sees all columns except admin-only ones
    return allAvailableColumns.filter(col => !adminOnlyColumns.includes(col.value))
  }, [isAdmin])

  // Default to showing key columns (filtered by role)
  const defaultColumns = useMemo(() => {
    const defaults = [
      'search_id', 'offense_id', 'charge', 'disposition',
      'ce_decision', 'ground_truth', 'segment', 'match_status', 'error_type', 'actions'
    ]

    // Filter out any columns the user doesn't have access to
    return defaults.filter(col =>
      availableColumnsForRole.some(available => available.value === col)
    )
  }, [availableColumnsForRole])

  const [selectedColumns, setSelectedColumns] = useState<string[]>(defaultColumns)

  // Fetch data
  const { selectedVersion } = useVersionContext()
  const { data: jurisdictions } = useJurisdictions()
  const { data: dashboard, isLoading, refetch } = useComplianceDashboard({
    startDate: dateRange[0]?.toISOString().split('T')[0] || '',
    endDate: dateRange[1]?.toISOString().split('T')[0] || '',
    jurisdiction: jurisdiction || undefined,
    page,
    pageSize,
    searchId,
    segment: segmentFilter,
    matchStatus: matchFilter,
    errorType: errorTypeFilter,
    mismatchCategory: mismatchCategoryFilter,
    disposition: dispositionFilter,
    crimeCategory: crimeCategoryFilter,
    adjudication: adjudicationFilter,
    courtType: courtTypeFilter,
    modelVersion: selectedVersion || undefined,
  })

  useEffect(() => {
    setPage(1)
  }, [
    dateRange,
    jurisdiction,
    searchId,
    segmentFilter,
    matchFilter,
    errorTypeFilter,
    mismatchCategoryFilter,
    dispositionFilter,
    crimeCategoryFilter,
    adjudicationFilter,
    courtTypeFilter,
    selectedVersion,
  ])

  const stats = dashboard?.metrics || null
  const filterOptions = dashboard?.filterOptions || {
    dispositions: [],
    crimeCategories: [],
    adjudications: [],
    courtTypes: [],
    mismatchCategories: [],
  }
  const rows = dashboard?.rows || []
  const totalCount = dashboard?.totalCount || 0

  const totalPages = Math.max(1, Math.ceil(totalCount / pageSize))

  // Download ALL filtered data as CSV (fetches all data from server)
  const downloadCSV = async () => {
    if (totalCount === 0) return

    setIsExporting(true)
    try {
      // Fetch ALL filtered data from export endpoint
      const exportResponse = await exportComplianceDashboard({
        startDate: dateRange[0]?.toISOString().split('T')[0] || '',
        endDate: dateRange[1]?.toISOString().split('T')[0] || '',
        jurisdiction: jurisdiction || undefined,
        page: 1,
        pageSize: 100000, // Large limit
        searchId,
        segment: segmentFilter,
        matchStatus: matchFilter,
        errorType: errorTypeFilter,
        mismatchCategory: mismatchCategoryFilter,
        disposition: dispositionFilter,
        crimeCategory: crimeCategoryFilter,
        adjudication: adjudicationFilter,
        courtType: courtTypeFilter,
      })

      // The API response is already unwrapped to { rows, totalCount }
      const allRows = exportResponse.rows || []
      if (allRows.length === 0) {
        setIsExporting(false)
        return
      }

      // Use only selected columns for export
      const columnsToExport = selectedColumns.filter(col => col !== 'actions') // Exclude actions column

      // Create CSV header with column labels
      const header = columnsToExport.map(col => {
        const columnDef = allAvailableColumns.find(c => c.value === col)
        return columnDef?.label || col
      }).join(',')

      // Helper to render value for CSV
      const renderValueForCSV = (value: any) => {
        if (value === null || value === undefined) return ''
        if (Array.isArray(value)) return value.join('; ')
        if (typeof value === 'object') return JSON.stringify(value)
        return String(value)
      }

      // Create CSV rows
      const csvRows = allRows.map((record: any) => {
        return columnsToExport.map(col => {
          const value = record[col]
          const stringValue = renderValueForCSV(value)
          // Escape quotes and wrap in quotes if contains comma or quotes
          if (stringValue.includes(',') || stringValue.includes('"') || stringValue.includes('\n')) {
            return `"${stringValue.replace(/"/g, '""')}"`
          }
          return stringValue
        }).join(',')
      })

      const csv = [header, ...csvRows].join('\n')
      const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' })
      const url = URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = url
      link.download = `compliance_export_${new Date().toISOString().slice(0, 10)}.csv`
      link.click()
      URL.revokeObjectURL(url)
    } catch (error) {
      console.error('Export failed:', error)
      alert('Export failed. Please try again.')
    } finally {
      setIsExporting(false)
    }
  }

  if (isLoading && !dashboard) {
    return (
      <Center style={{ minHeight: '60vh' }}>
        <Loader size="xl" />
      </Center>
    )
  }

  if (!dashboard || totalCount === 0) {
    const availableRange = dashboard?.availableDateRange
    return (
      <Container fluid p="md">
        <Stack gap="xl">
          <Title order={2}>Compliance Analytics</Title>
          <Text size="sm" c="dimmed">Offense-level reportability and classification analysis</Text>

          {/* Filters */}
          <Card shadow="sm" padding="lg" radius="md" withBorder>
            <Grid>
              <Grid.Col span={{ base: 12, md: 6 }}>
                <DatePickerInput
                  type="range"
                  label="Search Date Range"
                  placeholder="Select date range"
                  value={dateRange}
                  onChange={setDateRange}
                  leftSection={<IconCalendar size={16} />}
                  clearable
                />
              </Grid.Col>
              <Grid.Col span={{ base: 12, md: 6 }}>
                <Select
                  label="Jurisdiction"
                  placeholder="All jurisdictions"
                  data={jurisdictions?.map(j => ({ value: j, label: j })) || []}
                  value={jurisdiction}
                  onChange={setJurisdiction}
                  leftSection={<IconFilter size={16} />}
                  clearable
                />
              </Grid.Col>
            </Grid>
          </Card>

          <Center style={{ minHeight: '40vh' }}>
            <Stack align="center">
              <Text size="xl" c="dimmed">No data found for the selected filters</Text>
              <Text size="sm" c="dimmed">Try adjusting your date range or jurisdiction</Text>
              {availableRange?.minDate && availableRange?.maxDate && (
                <Text size="sm" c="dimmed">
                  Available data range: {availableRange.minDate} to {availableRange.maxDate}
                </Text>
              )}
              <Button onClick={() => refetch()} variant="light">
                Refresh
              </Button>
            </Stack>
          </Center>
        </Stack>
      </Container>
    )
  }

  return (
    <Container fluid p="md">
      <Stack gap="xl">
        <Group justify="space-between">
          <div>
            <Title order={2}>Compliance Analytics</Title>
            <Text size="sm" c="dimmed">Offense-level reportability and classification analysis</Text>
          </div>
          <Button leftSection={<IconRefresh size={16} />} onClick={() => refetch()} variant="light" size="sm">
            Refresh
          </Button>
        </Group>

        {/* Filters */}
        <Card shadow="sm" padding="lg" radius="md" withBorder>
          <Grid>
            <Grid.Col span={{ base: 12, md: 6 }}>
              <DatePickerInput
                type="range"
                label="Search Date Range"
                placeholder="Select date range"
                value={dateRange}
                onChange={setDateRange}
                leftSection={<IconCalendar size={16} />}
                clearable
              />
            </Grid.Col>
            <Grid.Col span={{ base: 12, md: 6 }}>
              <Select
                label="Jurisdiction"
                placeholder="All jurisdictions"
                data={jurisdictions?.map(j => ({ value: j, label: j })) || []}
                value={jurisdiction}
                onChange={setJurisdiction}
                leftSection={<IconFilter size={16} />}
                clearable
              />
            </Grid.Col>
          </Grid>
        </Card>

        {/* Executive Summary - Admin Only */}
        {stats && isAdmin && (
          <>
            <Title order={3}>Summary</Title>
            <Grid>
              {/* Accuracy Card - Large */}
              <Grid.Col span={{ base: 12, md: 4 }}>
                <Card
                  shadow="md"
                  padding="xl"
                  radius="md"
                  style={{
                    background: 'linear-gradient(135deg, #2ECC71 0%, #27AE60 100%)',
                    color: 'white',
                  }}
                >
                  <Stack align="center" gap="xs">
                    <Text size="5rem" fw={800} style={{ lineHeight: 1 }}>
                      {stats.accuracy.toFixed(1)}%
                    </Text>
                    <Text size="lg" fw={500}>
                      Adjusted Accuracy
                    </Text>
                    <Text size="sm" opacity={0.9}>
                      (Evaluable cases only)
                    </Text>
                  </Stack>
                </Card>
              </Grid.Col>

              {/* Other Metrics */}
              <Grid.Col span={{ base: 12, md: 8 }}>
                <Grid>
                  <Grid.Col span={6}>
                    <StatCard
                      value={fmt(stats.totalOffenses)}
                      label="Total Offenses"
                      color="#1A8AD4"
                    />
                  </Grid.Col>
                  <Grid.Col span={6}>
                    <StatCard
                      value={fmt(stats.evaluable)}
                      label="Evaluable"
                      color="#10B981"
                    />
                  </Grid.Col>
                  <Grid.Col span={6}>
                    <StatCard
                      value={fmt(stats.requiresResearch)}
                      label="Requires Research"
                      color="#F59E0B"
                    />
                  </Grid.Col>
                  <Grid.Col span={6}>
                    <StatCard
                      value={fmt(stats.ignoredByUbs)}
                      label="Excluded"
                      color="#EF4444"
                    />
                  </Grid.Col>
                </Grid>
              </Grid.Col>
            </Grid>

            {/* Confusion Matrix */}
            <Title order={3}>Classification Results</Title>
            <Grid>
              <Grid.Col span={{ base: 12, md: 6 }}>
                <Card shadow="sm" padding="lg" radius="md" withBorder>
                  <Stack gap="md">
                    <Text fw={600} size="lg">
                      Classification Results
                    </Text>
                    <Table highlightOnHover>
                      <Table.Thead>
                        <Table.Tr>
                          <Table.Th></Table.Th>
                          <Table.Th>Predicted Reportable</Table.Th>
                          <Table.Th>Predicted Non-Reportable</Table.Th>
                        </Table.Tr>
                      </Table.Thead>
                      <Table.Tbody>
                        <Table.Tr>
                          <Table.Td fw={600}>Actually Reportable</Table.Td>
                          <Table.Td>
                            <Badge color="green" size="lg">
                              {fmt(stats.truePositives)}
                            </Badge>
                            <Text size="xs" c="dimmed">
                              True Positives
                            </Text>
                          </Table.Td>
                          <Table.Td>
                            <Badge color="red" size="lg">
                              {fmt(stats.falseNegatives)}
                            </Badge>
                            <Text size="xs" c="dimmed">
                              False Negatives
                            </Text>
                          </Table.Td>
                        </Table.Tr>
                        <Table.Tr>
                          <Table.Td fw={600}>Actually Non-Reportable</Table.Td>
                          <Table.Td>
                            <Badge color="orange" size="lg">
                              {fmt(stats.falsePositives)}
                            </Badge>
                            <Text size="xs" c="dimmed">
                              False Positives
                            </Text>
                          </Table.Td>
                          <Table.Td>
                            <Badge color="green" size="lg">
                              {fmt(stats.trueNegatives)}
                            </Badge>
                            <Text size="xs" c="dimmed">
                              True Negatives
                            </Text>
                          </Table.Td>
                        </Table.Tr>
                      </Table.Tbody>
                    </Table>
                  </Stack>
                </Card>
              </Grid.Col>

              {/* Performance Metrics */}
              <Grid.Col span={{ base: 12, md: 6 }}>
                <Card shadow="sm" padding="lg" radius="md" withBorder>
                  <Stack gap="md">
                    <Text fw={600} size="lg">
                      Performance Metrics
                    </Text>
                    <Grid>
                      <Grid.Col span={6}>
                        <Paper p="md" withBorder>
                          <Stack gap="xs" align="center">
                            <Text size="2xl" fw={700} c="blue">
                              {stats.precision.toFixed(1)}%
                            </Text>
                            <Text size="sm" c="dimmed">
                              Precision
                            </Text>
                          </Stack>
                        </Paper>
                      </Grid.Col>
                      <Grid.Col span={6}>
                        <Paper p="md" withBorder>
                          <Stack gap="xs" align="center">
                            <Text size="2xl" fw={700} c="blue">
                              {stats.recall.toFixed(1)}%
                            </Text>
                            <Text size="sm" c="dimmed">
                              Recall
                            </Text>
                          </Stack>
                        </Paper>
                      </Grid.Col>
                      <Grid.Col span={12}>
                        <Paper p="md" withBorder>
                          <Stack gap="xs" align="center">
                            <Text size="2xl" fw={700} c="blue">
                              {stats.f1Score.toFixed(1)}%
                            </Text>
                            <Text size="sm" c="dimmed">
                              F1 Score
                            </Text>
                          </Stack>
                        </Paper>
                      </Grid.Col>
                    </Grid>
                  </Stack>
                </Card>
              </Grid.Col>
            </Grid>

            {/* Mismatch Analysis - Admin Only */}
            <Title order={3}>Mismatch Analysis</Title>
            <MismatchAnalysis stats={stats} />
          </>
        )}

        {/* Offense Explorer - Available to All Roles */}
        <Title order={3}>Offense Explorer</Title>
        <Card shadow="sm" padding="lg" radius="md" withBorder>
          <Stack gap="md">
                {/* Filters Row 1 */}
                <Grid>
                  <Grid.Col span={{ base: 12, md: 3 }}>
                    <TextInput
                      label="Search ID"
                      placeholder="Enter search ID..."
                      value={searchId}
                      onChange={(e) => setSearchId(e.currentTarget.value)}
                      leftSection={<IconSearch size={16} />}
                    />
                  </Grid.Col>
                  <Grid.Col span={{ base: 12, md: 3 }}>
                    <MultiSelect
                      label="Segment"
                      data={['EVALUABLE', 'REQUIRES_RESEARCH', 'IGNORED_BY_UBS']}
                      value={segmentFilter}
                      onChange={setSegmentFilter}
                      placeholder="All segments"
                      clearable
                    />
                  </Grid.Col>
                  <Grid.Col span={{ base: 12, md: 3 }}>
                    <MultiSelect
                      label="Match Status"
                      data={['MATCH', 'MISMATCH', 'MANUAL_REVIEW', 'UNKNOWN']}
                      value={matchFilter}
                      onChange={setMatchFilter}
                      placeholder="All statuses"
                      clearable
                    />
                  </Grid.Col>
                  <Grid.Col span={{ base: 12, md: 3 }}>
                    <MultiSelect
                      label="Error Type"
                      data={['CORRECT', 'FALSE_POSITIVE', 'FALSE_NEGATIVE', 'OTHER']}
                      value={errorTypeFilter}
                      onChange={setErrorTypeFilter}
                      placeholder="All error types"
                      clearable
                    />
                  </Grid.Col>
                </Grid>

                {/* Filters Row 1.5 - Error Categories */}
                <Grid>
                  <Grid.Col span={{ base: 12, md: 6 }}>
                    <MultiSelect
                      label="Filter by Error Category"
                      data={filterOptions.mismatchCategories}
                      value={mismatchCategoryFilter}
                      onChange={setMismatchCategoryFilter}
                      placeholder="All categories (only for mismatches)"
                      description="Categorizes mismatches into error types"
                      clearable
                    />
                  </Grid.Col>
                </Grid>

                {/* Filters Row 2 */}
                <Grid>
                  <Grid.Col span={{ base: 12, md: 3 }}>
                    <MultiSelect
                      label="Disposition"
                      data={filterOptions.dispositions}
                      value={dispositionFilter}
                      onChange={setDispositionFilter}
                      placeholder="All dispositions"
                      searchable
                      maxDropdownHeight={200}
                      clearable
                    />
                  </Grid.Col>
                  <Grid.Col span={{ base: 12, md: 3 }}>
                    <MultiSelect
                      label="Crime Category"
                      data={filterOptions.crimeCategories}
                      value={crimeCategoryFilter}
                      onChange={setCrimeCategoryFilter}
                      placeholder="All categories"
                      searchable
                      maxDropdownHeight={200}
                      clearable
                    />
                  </Grid.Col>
                  <Grid.Col span={{ base: 12, md: 3 }}>
                    <MultiSelect
                      label="Adjudication"
                      data={filterOptions.adjudications}
                      value={adjudicationFilter}
                      onChange={setAdjudicationFilter}
                      placeholder="All adjudications"
                      searchable
                      maxDropdownHeight={200}
                      clearable
                    />
                  </Grid.Col>
                  <Grid.Col span={{ base: 12, md: 3 }}>
                    <MultiSelect
                      label="Court Type"
                      data={filterOptions.courtTypes}
                      value={courtTypeFilter}
                      onChange={setCourtTypeFilter}
                      placeholder="All court types"
                      searchable
                      maxDropdownHeight={200}
                      clearable
                    />
                  </Grid.Col>
                </Grid>

                {/* Column Picker */}
                <MultiSelect
                  label="Select Columns to Display"
                  data={availableColumnsForRole}
                  value={selectedColumns}
                  onChange={setSelectedColumns}
                  placeholder="Choose columns..."
                  searchable
                  maxDropdownHeight={300}
                  description={`${selectedColumns.length} of ${availableColumnsForRole.length} columns selected`}
                />

                {/* Action Buttons */}
                <Group>
                  <Button
                    variant="outline"
                    onClick={() => {
                      setSearchId('')
                      setSegmentFilter([])
                      setMatchFilter([])
                      setErrorTypeFilter([])
                      setMismatchCategoryFilter([])
                      setDispositionFilter([])
                      setCrimeCategoryFilter([])
                      setAdjudicationFilter([])
                      setCourtTypeFilter([])
                      setPage(1)
                    }}
                  >
                    Clear Filters
                  </Button>
                  <Button
                    variant="outline"
                    onClick={() => setSelectedColumns(availableColumnsForRole.map(c => c.value))}
                  >
                    Select All Columns
                  </Button>

                  {/* Export button with role-based permissions */}
                  {canExportFilteredData ? (
                    <Tooltip label={`Export all ${fmt(totalCount)} filtered records`}>
                      <Button
                        leftSection={isExporting ? <IconLoader size={16} className="animate-spin" /> : <IconDownload size={16} />}
                        onClick={downloadCSV}
                        disabled={totalCount === 0 || isExporting}
                        loading={isExporting}
                      >
                        {isExporting ? 'Exporting...' : `Download All (${fmt(totalCount)} records)`}
                      </Button>
                    </Tooltip>
                  ) : (
                    <Tooltip label="You don't have permission to export data. Contact your administrator.">
                      <Button
                        leftSection={<IconLock size={16} />}
                        disabled
                        variant="outline"
                      >
                        Download CSV (No Permission)
                      </Button>
                    </Tooltip>
                  )}

                  {/* Admin-only: Export current page */}
                  <PermissionGate
                    permission="export:full-data"
                    fallback={null}
                  >
                    <Tooltip label="Export current page (paginated mode)">
                      <Button
                        leftSection={<IconDownload size={16} />}
                        onClick={() => {
                          // Export current page only (paginated)
                          if (!rows || rows.length === 0) return
                          const allColumnsExport = selectedColumns
                          const header = allColumnsExport.map(col => {
                            const columnDef = allAvailableColumns.find(c => c.value === col)
                            return columnDef?.label || col
                          }).join(',')

                          const renderValueForCSV = (value: any) => {
                            if (value === null || value === undefined) return ''
                            if (Array.isArray(value)) return value.join('; ')
                            if (typeof value === 'object') return JSON.stringify(value)
                            return String(value)
                          }

                          const csvRows = rows.map(record => {
                            return allColumnsExport.map(col => {
                              const value = (record as any)[col]
                              const stringValue = renderValueForCSV(value)
                              if (stringValue.includes(',') || stringValue.includes('"') || stringValue.includes('\n')) {
                                return `"${stringValue.replace(/"/g, '""')}"`
                              }
                              return stringValue
                            }).join(',')
                          })

                          const csv = [header, ...csvRows].join('\n')
                          const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' })
                          const url = URL.createObjectURL(blob)
                          const link = document.createElement('a')
                          link.href = url
                          link.download = `full_dataset_${new Date().toISOString().slice(0, 10)}.csv`
                          link.click()
                          URL.revokeObjectURL(url)
                        }}
                        disabled={!rows || rows.length === 0}
                        variant="filled"
                        color="red"
                        rightSection={<AdminOnlyBadge />}
                      >
                        Export Current Page ({rows.length.toLocaleString()} records)
                      </Button>
                    </Tooltip>
                  </PermissionGate>
                </Group>

                {/* Results Count and Pagination */}
                <Group justify="space-between">
                  <Text size="sm" c="dimmed">
                    Showing {rows?.length?.toLocaleString() || '0'} of {fmt(totalCount)} offenses (Page {page} of {totalPages})
                  </Text>
                  {totalPages > 1 && (
                    <Pagination
                      total={totalPages}
                      value={page}
                      onChange={setPage}
                      size="sm"
                    />
                  )}
                </Group>

                {/* Table */}
                <ScrollArea>
                  <Table striped highlightOnHover withTableBorder withColumnBorders>
                    <Table.Thead>
                      <Table.Tr>
                        {selectedColumns.map(colValue => {
                          const col = allAvailableColumns.find(c => c.value === colValue)
                          return <Table.Th key={colValue}>{col?.label || colValue}</Table.Th>
                        })}
                      </Table.Tr>
                    </Table.Thead>
                    <Table.Tbody>
                      {rows.map((record) => {
                        const segment = record.segment || 'EVALUABLE'
                        const errorType = record.error_type || 'OTHER'

                        // Helper to safely render values (convert arrays/objects to strings, truncate long text)
                        const renderValue = (value: any, colValue: string) => {
                          // Check for special columns first (before null/undefined check)

                          // Actions column (admin only)
                          if (colValue === 'actions') {
                            return isAdmin ? (
                              <SendToExpertButton offense={record} size="xs" />
                            ) : null
                          }

                          // For special badge columns, return badges instead of text
                          if (colValue === 'segment') {
                            return (
                              <Badge color={
                                segment === 'EVALUABLE' ? 'green' :
                                segment === 'REQUIRES_RESEARCH' ? 'orange' : 'red'
                              }>
                                {segment}
                              </Badge>
                            )
                          }

                          if (colValue === 'match_status') {
                            return (
                              <Badge color={record.match_status === 'MATCH' ? 'green' : 'red'}>
                                {record.match_status}
                              </Badge>
                            )
                          }

                          if (colValue === 'error_type') {
                            return (
                              <Badge color={
                                errorType === 'CORRECT' ? 'green' :
                                errorType === 'FALSE_POSITIVE' ? 'orange' :
                                errorType === 'FALSE_NEGATIVE' ? 'red' : 'gray'
                              }>
                                {errorType}
                              </Badge>
                            )
                          }

                          if (colValue === 'sentence_is_serving') {
                            if (value === null || value === undefined) return ''
                            return value ? 'Yes' : 'No'
                          }

                          if (colValue === 'effective_release_date') {
                            if (!value) return ''
                            try {
                              const dateValue = typeof value === 'object' && value?.value ? value.value : value
                              const date = new Date(dateValue)
                              if (isNaN(date.getTime())) return String(value)
                              return date.toLocaleDateString()
                            } catch {
                              return String(value)
                            }
                          }

                          // Now check for null/undefined values
                          if (value === null || value === undefined) return ''

                          let displayValue: string
                          if (Array.isArray(value)) {
                            displayValue = value.join(', ')
                          } else if (typeof value === 'object') {
                            displayValue = JSON.stringify(value)
                          } else {
                            displayValue = String(value)
                          }

                          // Truncate long text and add tooltip
                          const maxLength = 100
                          if (displayValue.length > maxLength) {
                            return (
                              <Text
                                size="sm"
                                lineClamp={2}
                                title={displayValue}
                                style={{ cursor: 'help', maxWidth: '300px' }}
                              >
                                {displayValue}
                              </Text>
                            )
                          }

                          return <Text size="sm">{displayValue}</Text>
                        }

                        return (
                          <Table.Tr
                            key={record.offense_id}
                            onClick={() => {
                              setSelectedOffense(record)
                              setDialogOpened(true)
                            }}
                            style={{ cursor: 'pointer' }}
                          >
                            {selectedColumns.map(colValue => (
                              <Table.Td
                                key={colValue}
                                style={{
                                  maxWidth: '300px',
                                  overflow: 'hidden',
                                  textOverflow: 'ellipsis',
                                  whiteSpace: 'nowrap'
                                }}
                              >
                                {renderValue((record as any)[colValue], colValue)}
                              </Table.Td>
                            ))}
                          </Table.Tr>
                        )
                      })}
                    </Table.Tbody>
                  </Table>
                </ScrollArea>

                {/* Bottom Pagination */}
                {totalPages > 1 && (
                  <Group justify="center">
                    <Pagination
                      total={totalPages}
                      value={page}
                      onChange={setPage}
                    />
                  </Group>
                )}
              </Stack>
            </Card>
      </Stack>

      {/* Offense Detail Dialog */}
      <OffenseDetailDialog
        opened={dialogOpened}
        onClose={() => {
          setDialogOpened(false)
          setSelectedOffense(null)
        }}
        offense={selectedOffense}
        allOffensesInSearch={offensesBySearchData?.rows || []}
      />
    </Container>
  )
}

// Helper component for metric cards
function StatCard({ value, label, color }: { value: string; label: string; color: string }) {
  return (
    <Card shadow="sm" padding="lg" radius="md" withBorder style={{ height: '100%' }}>
      <Stack align="center" gap="xs">
        <Text size="2rem" fw={700} style={{ color }}>
          {value}
        </Text>
        <Text size="sm" c="dimmed" ta="center">
          {label}
        </Text>
      </Stack>
    </Card>
  )
}

// Mismatch Analysis Component (Summary Only - No Table)
function MismatchAnalysis({ stats }: { stats: ComplianceStatistics | null }) {
  if (!stats) {
    return (
      <Text size="sm" c="dimmed">
        No mismatch data available for the selected date range.
      </Text>
    )
  }

  const totalMismatches = stats.totalMismatches || 0
  const evaluableWithDecision = stats.evaluableWithDecision || 0
  const errorRate = evaluableWithDecision > 0 ? (totalMismatches / evaluableWithDecision) * 100 : 0
  const breakdown = stats.mismatchBreakdown || []
  const totalValue = breakdown.reduce((sum, entry) => sum + (entry.count || 0), 0)

  const chartData = breakdown.map(entry => ({
    value: totalValue > 0 ? (entry.count / totalValue) * 100 : 0,
    count: entry.count,
    color: entry.category === 'Other'
      ? '#339AF0'
      : entry.category === 'Traffic Violation Mis-labeling'
        ? '#F59F00'
        : '#FA5252',
    label: entry.category,
  }))

  return (
    <Stack gap="lg">
      {/* Summary Cards */}
      <Grid>
        <Grid.Col span={4}>
          <StatCard
            value={fmt(totalMismatches)}
            label="Total Mismatches"
            color="#EF4444"
          />
        </Grid.Col>
        <Grid.Col span={4}>
          <StatCard
            value={`${errorRate.toFixed(1)}%`}
            label="Error Rate"
            color="#6B7280"
          />
        </Grid.Col>
        <Grid.Col span={4}>
          <StatCard
            value={fmt(evaluableWithDecision)}
            label="Evaluable w/ Decision"
            color="#10B981"
          />
        </Grid.Col>
      </Grid>

      {/* Charts and Details */}
      <Grid>
        <Grid.Col span={{ base: 12, md: 6 }}>
          <Card shadow="sm" padding="lg" radius="md" withBorder>
            <Stack gap="md" align="center">
              <Text fw={600} size="lg">Error Categories</Text>
              <RingProgress
                size={250}
                thickness={30}
                sections={chartData}
                label={
                  <Stack align="center" gap={0}>
                    <Text size="xl" fw={700}>{totalValue}</Text>
                    <Text size="xs" c="dimmed">Total Errors</Text>
                  </Stack>
                }
              />
              <Stack gap="xs" style={{ width: '100%' }}>
                {chartData.map((item, idx) => (
                  <Group key={idx} justify="space-between">
                    <Group gap="xs">
                      <div style={{
                        width: 12,
                        height: 12,
                        borderRadius: '50%',
                        backgroundColor: item.color
                      }} />
                      <Text size="sm">{item.label}</Text>
                    </Group>
                    <Text size="sm" fw={600}>
                      {item.count} ({item.value.toFixed(1)}%)
                    </Text>
                  </Group>
                ))}
              </Stack>
            </Stack>
          </Card>
        </Grid.Col>

        <Grid.Col span={{ base: 12, md: 6 }}>
          <Card shadow="sm" padding="lg" radius="md" withBorder>
            <Stack gap="md">
              <Text fw={600} size="lg">Category Details</Text>
              {breakdown.map(entry => {
                const pct = totalMismatches > 0 ? ((entry.count / totalMismatches) * 100).toFixed(1) : '0.0'
                return (
                  <Paper key={entry.category} p="md" withBorder>
                    <Stack gap="xs">
                      <Text fw={600}>{entry.category}</Text>
                      <Text size="sm" c="dimmed">
                        Total: {fmt(entry.count)} ({pct}% of errors)
                      </Text>
                    </Stack>
                  </Paper>
                )
              })}
            </Stack>
          </Card>
        </Grid.Col>
      </Grid>

      <Text size="sm" c="dimmed" ta="center">
        💡 Use the <strong>Filter by Error Category</strong> in the Offense Explorer below to view specific error types
      </Text>
    </Stack>
  )
}
